import type { BadgeColors } from "@/components/base/badges/badge-types";
import type {
  AnswerStatus,
  ResearchKeyword,
  SeoKeywordCanonicalRow,
  SeoKeywordResearchRow,
} from "@/types/admin";

// Source ranking mirrors the `seo_keyword_canonical` view (best volume source
// wins). Used to pick a stable representative base row per logical keyword.
const SOURCE_PRIORITY: Record<string, number> = {
  sistrix_csv: 1,
  atp_csv: 2,
  atp_api: 2,
  bing_api: 3,
};

function sourceRank(source: string): number {
  return SOURCE_PRIORITY[source] ?? 4;
}

// NUL-joined composite key — keywords/locales can't contain it, so no collisions.
function keyOf(keyword: string, language: string, country: string): string {
  return `${keyword} ${language} ${country}`;
}

/**
 * True when `a` is a better editorial representative than `b`. A question row
 * always wins (so a keyword that appears both as a PAA question and a head term
 * keeps its question fields); then lower source rank; then newer capture.
 */
function isBetterRepresentative(a: SeoKeywordResearchRow, b: SeoKeywordResearchRow): boolean {
  if (a.is_question !== b.is_question) return a.is_question;
  const ra = sourceRank(a.source);
  const rb = sourceRank(b.source);
  if (ra !== rb) return ra < rb;
  return (a.captured_at ?? "") > (b.captured_at ?? "");
}

/**
 * Collapse per-source `seo_keyword_research` rows to one logical keyword each,
 * merging the canonical search-volume / GSC aggregate. The representative row
 * supplies the `id` (writes target it) plus the editorial + question fields;
 * volume/cpc/competition/intent come from the canonical view.
 */
export function mergeResearchKeywords(
  rows: SeoKeywordResearchRow[],
  canonical: SeoKeywordCanonicalRow[],
): ResearchKeyword[] {
  const canon = new Map<string, SeoKeywordCanonicalRow>();
  for (const c of canonical) canon.set(keyOf(c.keyword, c.language, c.country), c);

  const reps = new Map<string, SeoKeywordResearchRow>();
  for (const r of rows) {
    const key = keyOf(r.keyword, r.language, r.country);
    const cur = reps.get(key);
    if (!cur || isBetterRepresentative(r, cur)) reps.set(key, r);
  }

  return [...reps.values()].map((r) => {
    const c = canon.get(keyOf(r.keyword, r.language, r.country));
    return {
      id: r.id,
      keyword: r.keyword,
      seed: r.seed,
      language: r.language,
      country: r.country,
      is_question: r.is_question,
      question_type: r.question_type,
      modifier: r.modifier,
      intent: c?.intent ?? r.intent,
      sentiment: c?.sentiment ?? r.sentiment,
      answer_status: r.answer_status,
      answer_mode: r.answer_mode,
      answered_url: r.answered_url,
      assigned_url: r.assigned_url,
      search_volume: c?.search_volume ?? null,
      search_volume_source: c?.search_volume_source ?? null,
      cpc: c?.cpc ?? null,
      competition: c?.competition ?? null,
      gsc_impressions: c?.gsc_impressions ?? null,
      gsc_clicks: c?.gsc_clicks ?? null,
      gsc_position: c?.gsc_position ?? null,
    };
  });
}

const INTENT_BADGE: Record<string, BadgeColors> = {
  informational: "blue",
  commercial: "indigo",
  transactional: "success",
  navigational: "gray",
};

/** Badge color for a search-intent value (free text; unknown → gray). */
export function intentBadge(intent: string | null | undefined): BadgeColors {
  return (intent && INTENT_BADGE[intent.toLowerCase()]) || "gray";
}

const ANSWER_STATUS_BADGE: Record<AnswerStatus, BadgeColors> = {
  none: "gray",
  planned: "blue",
  answered: "success",
  not_relevant: "warning",
};

/** Badge color for the answering-queue status of a research keyword. */
export function answerStatusBadge(status: AnswerStatus): BadgeColors {
  return ANSWER_STATUS_BADGE[status] ?? "gray";
}

/** Compact locale label, e.g. ("de","de") → "DE · DE", ("en","us") → "EN · US". */
export function localeLabel(language: string, country: string): string {
  return `${language.toUpperCase()} · ${country.toUpperCase()}`;
}

/** A keyword is "unassigned" until it has a target URL (the triage backlog). */
export function isUnassigned(k: Pick<ResearchKeyword, "assigned_url">): boolean {
  return !k.assigned_url;
}
